import { ApiError } from '../identity/types';
import { classifyFailure } from '../pay-employees/safeErrors';

/**
 * The screen never renders `err.message` (it can carry a server detail string or a request path). Every failure is reduced to a fixed,
 * plain-language sentence here, chosen by the server's stable `code` first and by the HTTP class otherwise.
 */
export interface CheckBatchFailure {
  /** 409 VERSION_CONFLICT: the batch changed elsewhere, so the screen must reload before the admin continues. */
  versionConflict: boolean;
  message: string;
}

const CODE_COPY: Record<string, string> = {
  VERSION_CONFLICT: 'This batch was changed by someone else. The latest version has been loaded — review it and try again.',
  CHECK_NUMBER_INVALID: 'Enter a check number of 1–20 letters, numbers or hyphens.',
  CHECK_NUMBER_DUPLICATE: 'That check number is already used. Enter a different check number.',
  BATCH_NOT_DRAFT: 'This batch can no longer be changed because it is not a draft.',
  READINESS_REQUIRED: 'Run the readiness check again before continuing — the batch changed since the last check.',
  READINESS_BLOCKED: 'Blocking issues remain. Resolve them and run the readiness check again.',
  WARNINGS_NOT_ACKNOWLEDGED: 'The warnings changed. Review them and acknowledge them again.',
  REVIEW_NOT_CONFIRMED: 'Confirm that you reviewed the payment details before approving.',
  RUN_NOT_APPROVED: 'The payroll run behind this batch is not approved.',
  NO_CHECK_EMPLOYEES: 'There are no employees paid by check in this payroll run.',
  FEATURE_NOT_AVAILABLE_IN_PRODUCTION: 'Approving a check batch is not available in this release.',
  SEPARATION_OF_DUTIES: 'A batch must be approved by someone other than the person who prepared it. Ask another person who can approve payments to review and approve it.',
};

export function describeFailure(err: unknown, fallback: string): CheckBatchFailure {
  if (err instanceof ApiError) {
    const known = err.code ? CODE_COPY[err.code] : undefined;
    if (known) return { versionConflict: err.code === 'VERSION_CONFLICT', message: known };
  }
  switch (classifyFailure(err)) {
    case 'forbidden':
      return { versionConflict: false, message: 'You don’t have permission to do this. Ask an administrator to grant you access.' };
    case 'session':
      return { versionConflict: false, message: 'Your session may have expired. Sign in again, then retry.' };
    case 'unavailable':
      return { versionConflict: false, message: 'A connected service isn’t responding right now. Try again shortly.' };
    default:
      return { versionConflict: false, message: fallback };
  }
}
